import { annotationBounds, DOCUMENT_UNITS, strokeOutline } from "./recordHandwritingGeometry";

export const RECORD_PDF_DOCUMENT_WIDTH = 960;
export const RECORD_PDF_PAGE_SIZE = [595.28, 841.89];
const margin = 28.35;
const renderScale = 2;
const imageTimeout = 15000;

export function recordPdfFilename(value = "講義與考卷_含手寫.pdf") {
  const name = Array.from(String(value), (character) => character.charCodeAt(0) < 32 || /[<>:"/\\|?*]/.test(character) ? "_" : character).join("").trim().slice(0, 180);
  return `${name.replace(/\.pdf$/i, "") || "講義與考卷_含手寫"}.pdf`;
}

// Preserve document coordinates: each page is a slice of the same unscaled paper.
export function planRecordPdfPages(height, pageHeight, protectedRanges = [], forcedBreaks = []) {
  if (!(height > 0) || !(pageHeight > 0)) throw new Error("無法取得講義內容，請重新開啟紀錄後再匯出。");
  const pages = [];
  let start = 0;
  while (start < height - 0.1) {
    let end = Math.min(height, start + pageHeight);
    const pageStart = start;
    const naturalEnd = end;
    const forced = forcedBreaks.filter((value) => value > pageStart + 1 && value < naturalEnd).sort((a, b) => a - b)[0];
    if (forced !== undefined) end = forced;
    let changed = true;
    while (changed && end < height) {
      changed = false;
      for (const range of protectedRanges) {
        if (range.top > start + 1 && range.top < end && range.bottom > end + 0.1 && range.bottom - range.top <= pageHeight) {
          end = range.top;
          changed = true;
        }
      }
    }
    if (end <= start + 1) end = Math.min(height, start + pageHeight);
    pages.push({ start, end });
    start = end;
  }
  return pages;
}

const waitFor = (promise, message) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error(message)), imageTimeout);
  Promise.resolve(promise).then((result) => { clearTimeout(timer); resolve(result); }, (error) => { clearTimeout(timer); reject(error); });
});

const blobDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("圖片讀取失敗，PDF 尚未匯出。"));
  reader.readAsDataURL(blob);
});

async function prepareImages(root) {
  const pending = root.querySelector('[data-record-export-image-state="pending"]');
  const failed = root.querySelector('[data-record-export-image-state="error"]');
  if (pending) throw new Error("圖片還在產生中，請待圖片顯示後再匯出 PDF。");
  if (failed) throw new Error("部分圖片產生失敗，請先修復圖片後再匯出 PDF。");
  const sources = new Map();
  await Promise.all(Array.from(root.querySelectorAll("img")).map(async (image) => {
    const source = image.currentSrc || image.src;
    if (!source) throw new Error("部分圖片尚未載入，請稍後再匯出 PDF。");
    image.loading = "eager";
    image.removeAttribute("srcset");
    image.removeAttribute("sizes");
    try {
      if (!sources.has(source)) sources.set(source, (async () => {
        if (source.startsWith("data:")) return source;
        const controller = new AbortController();
        try {
          return await waitFor((async () => {
            const response = await fetch(source, { signal: controller.signal, credentials: new URL(source, window.location.href).origin === window.location.origin ? "same-origin" : "omit" });
            if (!response.ok) throw new Error("image unavailable");
            return blobDataUrl(await response.blob());
          })(), "圖片載入逾時，請稍後再匯出 PDF。");
        } finally { controller.abort(); }
      })());
      image.src = await sources.get(source);
      await waitFor(image.decode ? image.decode() : new Promise((resolve, reject) => {
        if (image.complete && image.naturalWidth) resolve();
        else { image.onload = resolve; image.onerror = reject; }
      }), "圖片載入逾時，請稍後再匯出 PDF。");
      if (!image.naturalWidth) throw new Error("empty image");
      const style = window.getComputedStyle(image);
      const width = parseFloat(style.width); const height = parseFloat(style.height);
      // html2canvas does not apply object-fit; bake tall figures into their existing box.
      if (style.objectFit === "contain" && width > 0 && height > 0 && Math.abs(width / height - image.naturalWidth / image.naturalHeight) > 0.01) {
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(width * renderScale); canvas.height = Math.ceil(height * renderScale);
        const context = canvas.getContext("2d");
        const fit = Math.min(canvas.width / image.naturalWidth, canvas.height / image.naturalHeight);
        const drawnWidth = image.naturalWidth * fit; const drawnHeight = image.naturalHeight * fit;
        context.drawImage(image, (canvas.width - drawnWidth) / 2, (canvas.height - drawnHeight) / 2, drawnWidth, drawnHeight);
        image.style.width = `${width}px`; image.style.height = `${height}px`;
        image.src = canvas.toDataURL("image/png");
        await waitFor(image.decode(), "圖片載入逾時，請稍後再匯出 PDF。");
      }
    } catch (error) {
      throw new Error(`圖片「${image.alt || "教材圖片"}」無法讀取，PDF 尚未匯出。請確認圖片可正常顯示後重試。`);
    }
  }));
}

function clonePaper(documentElement, documentWidth = RECORD_PDF_DOCUMENT_WIDTH) {
  if (!documentElement) throw new Error("找不到講義內容，請重新開啟紀錄後再匯出。");
  // Clone before any await: a record switch must never change the export snapshot.
  const paper = documentElement.cloneNode(true);
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.dataset.recordPdfSnapshot = "true";
  Object.assign(host.style, { position: "absolute", left: "-100000px", top: "0", width: `${documentWidth}px`, pointerEvents: "none", background: "#fff" });
  Object.assign(paper.style, { width: `${documentWidth}px`, minWidth: `${documentWidth}px`, maxWidth: "none", transform: "none", margin: "0", color: "#27313c", background: "#fff", overflow: "visible" });
  for (const [name, value] of Object.entries({ "--app-surface": "#fff", "--app-text": "#27313c", "--app-surface-text": "#27313c", "--app-surface-soft": "#f0f0f0", "--app-border": "#d7dee4", "--app-input-bg": "#fff", "--app-input-text": "#27313c", "--app-muted-text": "#687380" })) paper.style.setProperty(name, value);
  paper.querySelectorAll('button, [data-record-export-exclude], [role="status"], [role="alert"]').forEach((element) => { element.style.visibility = "hidden"; });
  paper.querySelectorAll("[data-record-page-break]").forEach((element) => { element.style.visibility = "hidden"; });
  // Reveal horizontally scrolling equations without changing their paper coordinates.
  paper.querySelectorAll(".math-display").forEach((element) => { element.style.overflow = "visible"; element.scrollLeft = 0; });
  host.appendChild(paper);
  document.body.appendChild(host);
  return { paper, host };
}

function paperBreaks(paper, annotations, height, noteCanvases) {
  const origin = paper.getBoundingClientRect().top;
  const protectedRanges = [];
  const protect = (element) => {
    const box = element.getBoundingClientRect();
    if (box.height > 0) protectedRanges.push({ top: Math.max(0, box.top - origin), bottom: box.bottom - origin });
  };
  paper.querySelectorAll('[data-handout-question-uuid], [data-record-question], [data-record-block], figure, img, tr, pre, .math-display, .math-line, p, li').forEach(protect);
  paper.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((heading) => {
    const box = heading.getBoundingClientRect();
    let next = heading.nextElementSibling;
    while (next && !next.getBoundingClientRect().height) next = next.nextElementSibling;
    const nextBox = next?.getBoundingClientRect();
    protectedRanges.push({ top: Math.max(0, box.top - origin), bottom: nextBox ? Math.min(nextBox.bottom, nextBox.top + 32) - origin : box.bottom - origin });
  });
  // Oversized paragraphs/questions can still break between rendered text lines.
  const walker = document.createTreeWalker(paper, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    if (!walker.currentNode.textContent.trim()) continue;
    const range = document.createRange();
    range.selectNodeContents(walker.currentNode);
    for (const box of Array.from(range.getClientRects())) if (box.height > 0) protectedRanges.push({ top: Math.max(0, box.top - origin - 1), bottom: box.bottom - origin + 1 });
  }
  annotations.forEach((entry) => {
    const box = entry.type === "text" ? { x: entry.x, y: entry.y, ...noteCanvases.get(entry.id) } : annotationBounds(entry);
    protectedRanges.push({ top: box.y / DOCUMENT_UNITS * RECORD_PDF_DOCUMENT_WIDTH, bottom: Math.min(height, (box.y + box.height) / DOCUMENT_UNITS * RECORD_PDF_DOCUMENT_WIDTH) });
  });
  const forcedBreaks = Array.from(paper.querySelectorAll("[data-record-page-break]")).map((element) => element.getBoundingClientRect().bottom - origin);
  return { protectedRanges, forcedBreaks };
}

function textNoteCanvas(entry) {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("此瀏覽器無法輸出手寫文字，請使用其他瀏覽器重試。");
  const scale = renderScale * RECORD_PDF_DOCUMENT_WIDTH / DOCUMENT_UNITS;
  context.font = `${entry.fontSize * scale}px sans-serif`;
  const lines = entry.text.split("\n");
  const measured = Math.max(1, ...lines.map((line) => context.measureText(line).width));
  canvas.width = Math.max(1, Math.ceil(measured + entry.fontSize * scale * 0.2));
  canvas.height = Math.max(1, Math.ceil((lines.length * 1.3 + 0.2) * entry.fontSize * scale));
  context.font = `${entry.fontSize * scale}px sans-serif`;
  context.fillStyle = entry.color;
  context.textBaseline = "alphabetic";
  lines.forEach((line, index) => context.fillText(line, 0, entry.fontSize * scale * (1 + index * 1.3)));
  return { canvas, width: canvas.width / scale, height: canvas.height / scale };
}

export async function createRecordHandwritingPdf({ documentElement, annotations = [], title = "講義與考卷" }) {
  const { paper, host } = clonePaper(documentElement);
  try {
    if (document.fonts?.ready) await waitFor(document.fonts.ready, "字型載入逾時，請稍後再匯出 PDF。");
    await prepareImages(paper);
    const [{ default: html2canvas }, pdfLib] = await Promise.all([import("html2canvas"), import("pdf-lib")]);
    const { PDFDocument, StandardFonts, rgb, BlendMode, pushGraphicsState, popGraphicsState, moveTo, lineTo, closePath, clip, endPath } = pdfLib;
    const noteCanvases = new Map(annotations.filter((item) => item.type === "text").map((entry) => [entry.id, textNoteCanvas(entry)]));
    const box = paper.getBoundingClientRect();
    const inkBounds = annotations.map((entry) => entry.type === "text" ? { x: entry.x, y: entry.y, ...noteCanvases.get(entry.id) } : annotationBounds(entry));
    const wideMath = Array.from(paper.querySelectorAll(".math-line")).map((element) => element.getBoundingClientRect().right - box.left);
    // Include notes moved past a page edge; the original document never reflows.
    const left = Math.floor(Math.min(0, ...inkBounds.map((entry) => entry.x * RECORD_PDF_DOCUMENT_WIDTH / DOCUMENT_UNITS)));
    const top = Math.floor(Math.min(0, ...inkBounds.map((entry) => entry.y * RECORD_PDF_DOCUMENT_WIDTH / DOCUMENT_UNITS)));
    const captureWidth = Math.ceil(Math.max(RECORD_PDF_DOCUMENT_WIDTH, ...wideMath, ...inkBounds.map((entry) => (entry.x + entry.width) * RECORD_PDF_DOCUMENT_WIDTH / DOCUMENT_UNITS)) - left);
    const height = Math.ceil(Math.max(paper.scrollHeight, paper.offsetHeight, ...inkBounds.map((entry) => (entry.y + entry.height) * RECORD_PDF_DOCUMENT_WIDTH / DOCUMENT_UNITS)));
    if (captureWidth > 4096) throw new Error("部分公式或註記過長，請先將內容分行或縮小文字後再匯出 PDF。");
    const contentWidth = RECORD_PDF_PAGE_SIZE[0] - margin * 2;
    const pointPerPixel = contentWidth / captureWidth;
    const pointPerUnit = pointPerPixel * RECORD_PDF_DOCUMENT_WIDTH / DOCUMENT_UNITS;
    const maxPageHeight = (RECORD_PDF_PAGE_SIZE[1] - margin * 2) / pointPerPixel;
    // Keep every A4 page near 1920 × 2800 pixels, even with wide margin notes.
    const captureScale = renderScale * RECORD_PDF_DOCUMENT_WIDTH / captureWidth;
    const breaks = paperBreaks(paper, annotations, height, noteCanvases);
    const pages = planRecordPdfPages(height - top, maxPageHeight,
      breaks.protectedRanges.map((range) => ({ top: range.top - top, bottom: range.bottom - top })),
      breaks.forcedBreaks.map((value) => value - top)).map((slice) => ({ start: slice.start + top, end: slice.end + top }));
    const pdf = await PDFDocument.create();
    pdf.setTitle(title);
    pdf.setCreator("my-web");
    const footerFont = await pdf.embedFont(StandardFonts.Helvetica);
    const noteImages = new Map();
    for (const entry of annotations.filter((item) => item.type === "text")) {
      const note = noteCanvases.get(entry.id);
      noteImages.set(entry.id, { ...note, image: await pdf.embedPng(note.canvas.toDataURL("image/png")) });
    }
    for (const [index, slice] of pages.entries()) {
      const canvas = await html2canvas(paper, { backgroundColor: "#fff", scale: captureScale, width: captureWidth, height: Math.ceil(slice.end - slice.start), x: left, y: slice.start, scrollX: 0, scrollY: 0, windowWidth: RECORD_PDF_DOCUMENT_WIDTH, windowHeight: Math.ceil(maxPageHeight), useCORS: true, allowTaint: false, logging: false, imageTimeout });
      const image = await pdf.embedPng(canvas.toDataURL("image/png"));
      const page = pdf.addPage(RECORD_PDF_PAGE_SIZE);
      const contentHeight = (slice.end - slice.start) * pointPerPixel;
      page.drawImage(image, { x: margin, y: RECORD_PDF_PAGE_SIZE[1] - margin - contentHeight, width: contentWidth, height: contentHeight });
      const bottom = RECORD_PDF_PAGE_SIZE[1] - margin - contentHeight;
      page.pushOperators(pushGraphicsState(), moveTo(margin, bottom), lineTo(margin + contentWidth, bottom), lineTo(margin + contentWidth, bottom + contentHeight), lineTo(margin, bottom + contentHeight), closePath(), clip(), endPath());
      const originY = RECORD_PDF_PAGE_SIZE[1] - margin + slice.start * pointPerPixel;
      const originX = margin - left * pointPerPixel;
      for (const entry of annotations) {
        const bounds = entry.type === "text" ? { x: entry.x, y: entry.y, ...noteCanvases.get(entry.id) } : annotationBounds(entry);
        if ((bounds.y + bounds.height) * RECORD_PDF_DOCUMENT_WIDTH / DOCUMENT_UNITS < slice.start || bounds.y * RECORD_PDF_DOCUMENT_WIDTH / DOCUMENT_UNITS > slice.end) continue;
        if (entry.type === "stroke") {
          const hex = entry.color.slice(1);
          page.drawSvgPath(strokeOutline(entry), { x: originX, y: originY, scale: pointPerUnit, color: rgb(parseInt(hex.slice(0, 2), 16) / 255, parseInt(hex.slice(2, 4), 16) / 255, parseInt(hex.slice(4, 6), 16) / 255), opacity: entry.tool === "highlighter" ? 0.33 : entry.tool === "pencil" ? 0.72 : 1, blendMode: entry.tool === "highlighter" ? BlendMode.Multiply : BlendMode.Normal });
        } else {
          const note = noteImages.get(entry.id);
          page.drawImage(note.image, { x: originX + entry.x * pointPerUnit, y: originY - (entry.y + note.height) * pointPerUnit, width: note.width * pointPerUnit, height: note.height * pointPerUnit });
        }
      }
      page.pushOperators(popGraphicsState());
      page.drawText(`${index + 1} / ${pages.length}`, { x: RECORD_PDF_PAGE_SIZE[0] / 2 - 12, y: 12, size: 8, font: footerFont, color: rgb(0.45, 0.45, 0.45) });
      canvas.width = 1; canvas.height = 1;
    }
    return { bytes: await pdf.save(), pageCount: pages.length };
  } finally { host.remove(); }
}

export async function exportRecordHandwritingPdf(options) {
  const { bytes, pageCount } = await createRecordHandwritingPdf(options);
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = recordPdfFilename(options.filename || `${options.title || "講義與考卷"}_含手寫.pdf`);
  document.body.appendChild(link);
  try { link.click(); } finally { link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000); }
  return { pageCount };
}

// html2canvas 1.x cannot parse modern computed color()/oklch() values.
function normalizeCaptureColors(root) {
  const sample = document.createElement("canvas"); sample.width = 1; sample.height = 1;
  const context = sample.getContext("2d");
  if (!context) return;
  const colors = new Map();
  const compatibleColor = (value) => {
    if (colors.has(value)) return colors.get(value);
    context.clearRect(0, 0, 1, 1); context.fillStyle = value; context.fillRect(0, 0, 1, 1);
    const [r, g, b, a] = context.getImageData(0, 0, 1, 1).data;
    const result = `rgba(${r},${g},${b},${a / 255})`; colors.set(value, result); return result;
  };
  const properties = ["color", "background-color", "background-image", "border-top-color", "border-right-color", "border-bottom-color", "border-left-color", "outline-color", "text-decoration-color", "box-shadow", "text-shadow", "fill", "stroke"];
  for (const element of [root, ...root.querySelectorAll("*")]) {
    const computed = window.getComputedStyle(element);
    for (const property of properties) {
      const value = computed.getPropertyValue(property);
      if (/(?:color|oklch|oklab|lab|lch)\(/.test(value)) element.style.setProperty(property, value.replace(/(?:color|oklch|oklab|lab|lch)\([^()]*\)/g, compatibleColor));
    }
  }
}

export async function exportHandwritingSelection({ documentElement, annotations, bounds, title }) {
  const documentWidth = documentElement?.offsetWidth || RECORD_PDF_DOCUMENT_WIDTH;
  const { paper, host } = clonePaper(documentElement, documentWidth);
  try {
    if (document.fonts?.ready) await waitFor(document.fonts.ready, "字型載入逾時，請稍後再試。");
    await prepareImages(paper);
    const { default: html2canvas } = await import("html2canvas");
    normalizeCaptureColors(paper);
    const unit = documentWidth / DOCUMENT_UNITS;
    const width = Math.ceil(bounds.width * unit); const height = Math.ceil(bounds.height * unit);
    if (width > 4096 || height > 8192) throw new Error("選取範圍太大，請縮小區塊後再截圖。");
    const canvas = await html2canvas(paper, { x: bounds.x * unit, y: bounds.y * unit, width, height, scale: 2, backgroundColor: "#fff", scrollX: 0, scrollY: 0, useCORS: true, logging: false, imageTimeout });
    const context = canvas.getContext("2d");
    if (!context) throw new Error("無法建立區塊截圖。");
    // html2canvas leaves its crop/scale transform on the returned context.
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.globalAlpha = 1;
    context.save();
    context.scale(unit * 2, unit * 2);
    context.translate(-bounds.x, -bounds.y);
    annotations.filter((entry) => entry.type === "stroke").forEach((entry) => {
      context.fillStyle = entry.color;
      context.globalAlpha = entry.tool === "highlighter" ? 0.33 : entry.tool === "pencil" ? 0.72 : 1;
      context.fill(new Path2D(strokeOutline(entry)), "nonzero");
    });
    context.restore();
    annotations.filter((entry) => entry.type === "text").forEach((entry) => {
      const note = textNoteCanvas(entry);
      const ratio = documentWidth / RECORD_PDF_DOCUMENT_WIDTH;
      context.drawImage(note.canvas, (entry.x - bounds.x) * unit * 2, (entry.y - bounds.y) * unit * 2, note.canvas.width * ratio, note.canvas.height * ratio);
    });
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = recordPdfFilename(`${title || "題目"}_區塊截圖`).replace(/\.pdf$/, ".png");
    document.body.appendChild(link);
    try { link.click(); } finally { link.remove(); }
  } finally { host.remove(); }
}
